<template>
  <div class="ov-public-wrap">
    <div class="ov-public-card">
      <div v-if="loading" class="text-center text-secondary py-5">
        <span class="spinner-border spinner-border-sm mr-2"></span>Loading…
      </div>

      <div v-else-if="invalidLink" class="text-center py-5">
        <i class="bi bi-link-45deg" style="font-size:32px;color:#cbd5e1;"></i>
        <p class="text-dark mt-3 mb-0" style="font-weight:600;">This link is no longer valid.</p>
        <p class="text-secondary" style="font-size:13px;">Ask whoever shared it for a new one.</p>
      </div>

      <div v-else-if="submitted" class="text-center py-5">
        <i class="bi bi-check-circle-fill" style="font-size:40px;color:#fd4719;"></i>
        <p class="text-dark mt-3 mb-1" style="font-weight:700;font-size:18px;">Request sent</p>
        <p class="text-secondary mb-0" style="font-size:13px;">{{ project.company_name }} will review it shortly.</p>
      </div>

      <template v-else>
        <p class="text-secondary mb-1" style="font-size:12px;text-transform:uppercase;letter-spacing:0.4px;">Materials request</p>
        <h4 class="text-dark mb-4" style="font-weight:700;">{{ project.project_title }}</h4>

        <div v-if="formError" class="alert alert-danger py-2" style="font-size:13px;">{{ formError }}</div>

        <div class="form-group">
          <label>Your name</label>
          <input type="text" class="form-control" v-model="form.requester_name" placeholder="e.g. Juma, site manager">
        </div>
        <div class="form-group">
          <label>Phone or email <span class="text-secondary">(optional)</span></label>
          <input type="text" class="form-control" v-model="form.requester_contact" placeholder="So the team can reach you">
        </div>

        <label class="mb-2">What do you need?</label>
        <div v-for="(item, idx) in form.items" :key="idx" class="ov-item-row">
          <input type="text" class="form-control" v-model="item.name" placeholder="Item, e.g. Cement">
          <input type="number" min="0.01" step="any" class="form-control ov-item-qty" v-model="item.quantity" placeholder="Qty">
          <input type="text" class="form-control ov-item-unit" v-model="item.unit" placeholder="Unit">
          <button type="button" class="btn btn-link text-danger" @click="removeItem(idx)" v-if="form.items.length > 1">
            <i class="bi bi-trash"></i>
          </button>
        </div>
        <button type="button" class="btn btn-outline-secondary btn-sm mb-4" @click="addItem">
          <i class="bi bi-plus-lg mr-1"></i>Add another item
        </button>

        <div class="form-group">
          <label>Anything else the team should know? <span class="text-secondary">(optional)</span></label>
          <textarea class="form-control" rows="3" v-model="form.notes" placeholder="e.g. Needed by Friday for the foundation pour."></textarea>
        </div>

        <button class="btn btn-primary btn-block" style="border-radius:8px;" :disabled="submitting" @click="submit">
          <span v-if="submitting"><span class="spinner-border spinner-border-sm mr-1"></span>Sending…</span>
          <span v-else>Send request</span>
        </button>
      </template>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'PublicRequisitionForm',

  props: { token: { type: String, required: true } },

  data() {
    return {
      loading: true,
      invalidLink: false,
      submitting: false,
      submitted: false,
      formError: '',
      project: {},
      form: {
        requester_name: '',
        requester_contact: '',
        notes: '',
        items: [{ name: '', quantity: '', unit: '' }],
      },
    };
  },

  mounted() {
    this.fetchInfo();
  },

  methods: {
    async fetchInfo() {
      try {
        const { data } = await axios.get(`/api/public/project-requisition/${this.token}`);
        this.project = data;
      } catch (e) {
        this.invalidLink = true;
      } finally {
        this.loading = false;
      }
    },

    addItem() {
      this.form.items.push({ name: '', quantity: '', unit: '' });
    },

    removeItem(idx) {
      this.form.items.splice(idx, 1);
    },

    async submit() {
      this.formError = '';
      if (!this.form.requester_name) {
        this.formError = 'Enter your name so the team knows who this is from.';
        return;
      }
      const items = this.form.items.filter(i => i.name && i.quantity);
      if (items.length === 0) {
        this.formError = 'Add at least one item with a quantity.';
        return;
      }

      this.submitting = true;
      try {
        await axios.post(`/api/public/project-requisition/${this.token}`, {
          requester_name: this.form.requester_name,
          requester_contact: this.form.requester_contact || undefined,
          notes: this.form.notes || undefined,
          items,
        });
        this.submitted = true;
      } catch (err) {
        this.formError = err.response?.data?.message || 'Could not send this request. Please try again.';
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style scoped>
.ov-public-wrap {
  min-height: 100vh;
  background: #f8fafc;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 40px 16px;
}
.ov-public-card {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 16px;
  padding: 32px;
  width: 100%;
  max-width: 460px;
}
.ov-item-row {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
  align-items: center;
}
.ov-item-qty {
  max-width: 80px;
  flex-shrink: 0;
}
.ov-item-unit {
  max-width: 90px;
  flex-shrink: 0;
}
</style>
